import {
  BuildingOffice2Icon,
  ComputerDesktopIcon,
  EnvelopeIcon,
  IdentificationIcon,
  MapPinIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { memo } from 'react';

import type { FirmDetailLiteRead } from '@/services/api/generated/firmsService';
import { enhancedApi } from '@/services/api/generated/firmsService';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  createStatusBadge,
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';
import { UserLink } from './UserLink';

/**
 * Firm entity type (re-export from generated types)
 * Extended with ein field until backend PR is merged and types are regenerated
 */
export type Firm = FirmDetailLiteRead & {
  ein?: string | null;
};

/**
 * Firm tooltip content component
 * Pure presentation component - memoized for performance
 */
export const FirmTooltipContent = memo<EntityTooltipContentProps<Firm>>(
  ({ fetchState }) => {
    // Handle loading state
    if (fetchState.status === 'pending') {
      return <LoadingState rows={1} />;
    }

    // Handle error state
    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="firm" />;
    }

    // Handle idle or empty state - no data yet
    if (fetchState.status === 'idle') {
      return <EmptyState entityType="firm" />;
    }

    // Success state - TypeScript knows fetchState.data exists
    const firm = fetchState.data;
    const {
      name,
      type,
      status,
      managing_paralegal,
      primary_state,
      email,
      cms_system,
      cms_link,
      address,
      ein,
      front_channel,
    } = firm;

    const frontEmail =
      front_channel && typeof front_channel === 'object'
        ? (front_channel as { address?: string | null }).address
        : null;

    const paralegalName =
      managing_paralegal?.first_name || managing_paralegal?.last_name
        ? `${managing_paralegal.first_name || ''} ${managing_paralegal.last_name || ''}`.trim()
        : managing_paralegal?.username || null;

    // Format address as a single line
    const formattedAddress = address
      ? [
          address.line1,
          address.line2,
          [address.city, address.state].filter(Boolean).join(', '),
          address.postcode,
        ]
          .filter(Boolean)
          .join(', ')
      : null;

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader icon={BuildingOffice2Icon} title={name} />

        <TooltipSection withBorder>
          {(type || status) && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {type &&
                createStatusBadge({ value: type, defaultColor: 'purple' })}
              {status &&
                createStatusBadge({ value: status, defaultColor: 'zinc' })}
            </div>
          )}
          {email && <InfoRow icon={EnvelopeIcon} label="Email" value={email} />}
          {primary_state && (
            <InfoRow icon={MapPinIcon} label="State" value={primary_state} />
          )}
          {ein && <InfoRow icon={IdentificationIcon} label="EIN" value={ein} />}
          {frontEmail && (
            <InfoRow
              icon={EnvelopeIcon}
              label="Front Email"
              value={frontEmail}
              wrapValue
              valueClassName="truncate max-w-[200px]"
            />
          )}
          {managing_paralegal && paralegalName && (
            <InfoRow
              icon={UserCircleIcon}
              label="Managing Paralegal"
              value={
                <UserLink entityId={managing_paralegal.id} side="left">
                  {paralegalName}
                </UserLink>
              }
            />
          )}
          {cms_system && (
            <InfoRow
              icon={ComputerDesktopIcon}
              label="CMS"
              value={
                cms_link ? (
                  <a
                    href={cms_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {cms_system}
                  </a>
                ) : (
                  cms_system
                )
              }
            />
          )}
          {formattedAddress && (
            <InfoRow
              icon={MapPinIcon}
              label="Address"
              value={formattedAddress}
            />
          )}
        </TooltipSection>
      </div>
    );
  },
);

FirmTooltipContent.displayName = 'FirmTooltipContent';

/**
 * Hoverable firm link with tooltip preview
 *
 * Shows firm name, type, status, and managing paralegal on hover.
 * Click to navigate to firm detail page.
 *
 * @example
 * ```tsx
 * <FirmLink entityId={firmId}>Acme Law Firm</FirmLink>
 * ```
 */
export const FirmLink = createEntityLink<Firm, { id: string }>({
  tooltipContent: FirmTooltipContent,
  getDetailUrl: (id) => `/firms/${id}`,
  useQuery: enhancedApi.endpoints.firmsRetrieve.useQuery,
  getQueryArg: (id) => ({ id }),
  displayName: 'FirmLink',
});
